import axios from "axios";

const DEFAULT_API_URL =
  process.env.NODE_ENV === "production" ? process.env.REACT_APP_API_URL : "http://localhost:8000";

export const API_URL = (
  process.env.REACT_APP_API_URL || DEFAULT_API_URL
).replace(/\/+$/, "");

const API = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  withCredentials: true,
  withXSRFToken: true,
  timeout: 30000,
});

// In-flight Promise deduplication map & short-lived client-side cache
const inFlightRequests = new Map();
const memoryCache = new Map();

const CACHE_TTL_MS = 20000; // 20 seconds TTL for idempotent public queries

const CACHEABLE_PREFIXES = [
  "/categories",
  "/banners",
  "/prime/plan",
  "/prime/offers",
  "/prime/categories",
  "/subscriptions",
];

const isCacheableUrl = (url = "") => {
  return CACHEABLE_PREFIXES.some((prefix) => url.startsWith(prefix));
};

export const clearApiCache = () => {
  memoryCache.clear();
  inFlightRequests.clear();
};

API.interceptors.request.use(
  (config) => {
    const adminToken = localStorage.getItem("magizh_admin_token");
    const userToken = localStorage.getItem("magizh_token");

    const token = adminToken || userToken;

    if (token) {
      if (config.headers && typeof config.headers.set === "function") {
        config.headers.set("Authorization", `Bearer ${token}`);
      } else {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // Invalidate cached GET queries on write operations to ensure fresh state
    const method = String(config.method || "get").toLowerCase();
    if (method !== "get") {
      clearApiCache();
    }

    return config;
  },
  (error) => Promise.reject(error)
);

API.interceptors.response.use(
  (response) => {
    // Cache successful idempotent responses
    const method = String(response.config?.method || "get").toLowerCase();
    const url = String(response.config?.url || "");

    if (method === "get" && isCacheableUrl(url) && response.status === 200) {
      const token = localStorage.getItem("magizh_admin_token") || localStorage.getItem("magizh_token") || "anon";
      const cacheKey = `${token}:${url}:${JSON.stringify(response.config?.params || {})}`;
      memoryCache.set(cacheKey, {
        timestamp: Date.now(),
        data: response.data,
      });
    }

    return response;
  },
  (error) => {
    const status = error.response?.status;
    const responseData = error.response?.data;

    if (status === 401) {
      console.warn("Authentication required or session expired.");
      clearApiCache();
    }

    if (status === 403) {
      console.warn("You do not have permission for this action.");
    }

    if (status === 419) {
      console.warn("CSRF session expired. Please login again.");
      clearApiCache();
    }

    if (status === 422) {
      console.warn("Validation error:", responseData?.errors);
    }

    if (status === 429) {
      console.warn(
        "Security throttle: Too many requests. Please wait one minute."
      );
    }

    if (status >= 500) {
      console.error(
        "Backend server error:",
        responseData || error.message
      );
    }

    if (!error.response) {
      console.error(
        "Network error. Backend server may be unreachable:",
        error.message
      );
    }

    return Promise.reject(error);
  }
);

// High performance cached API.get wrapper with in-flight deduplication
const originalGet = API.get.bind(API);

API.get = function (url, config = {}) {
  const token = localStorage.getItem("magizh_admin_token") || localStorage.getItem("magizh_token") || "anon";
  const paramsKey = JSON.stringify(config.params || {});
  const cacheKey = `${token}:${url}:${paramsKey}`;

  // 1. Check Memory Cache if endpoint is cacheable
  if (isCacheableUrl(url) && !config.skipCache) {
    const cached = memoryCache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp < CACHE_TTL_MS)) {
      return Promise.resolve({
        data: cached.data,
        status: 200,
        statusText: "OK (Cached)",
        headers: {},
        config,
      });
    }
  }

  // 2. Coalesce in-flight concurrent requests to the same endpoint
  if (inFlightRequests.has(cacheKey)) {
    return inFlightRequests.get(cacheKey);
  }

  const promise = originalGet(url, config)
    .finally(() => {
      inFlightRequests.delete(cacheKey);
    });

  inFlightRequests.set(cacheKey, promise);
  return promise;
};

// Memoized CSRF Cookie fetcher to avoid repeated network calls
let csrfPromise = null;
let csrfFetchedAt = 0;

export const fetchCsrfCookie = async () => {
  const now = Date.now();
  if (csrfPromise && (now - csrfFetchedAt < 180000)) {
    return csrfPromise;
  }
  csrfFetchedAt = now;
  csrfPromise = axios.get(`${API_URL}/sanctum/csrf-cookie`, {
    headers: {
      Accept: "application/json",
    },
    withCredentials: true,
    withXSRFToken: true,
    timeout: 5000,
  }).catch(() => null);

  return csrfPromise;
};

export const registerUser = async (data) => {
  await fetchCsrfCookie();
  return API.post("/register", data);
};

export const sendRegisterOtp = async (data) => {
  await fetchCsrfCookie();
  return API.post("/register/send-otp", data);
};

export const verifyRegisterOtp = async (data) => {
  await fetchCsrfCookie();
  return API.post("/register/verify-otp", data);
};

export const resendRegisterOtp = async (data) => {
  await fetchCsrfCookie();
  return API.post("/register/resend-otp", data);
};

export const loginUser = async (data) => {
  await fetchCsrfCookie();
  return API.post("/login", data);
};

export const sendForgotPasswordOtp = async (data) => {
  await fetchCsrfCookie();
  return API.post("/forgot-password/send-otp", data);
};

export const verifyForgotPasswordOtp = async (data) => {
  await fetchCsrfCookie();
  return API.post("/forgot-password/verify-otp", data);
};

export const resendForgotPasswordOtp = async (data) => {
  await fetchCsrfCookie();
  return API.post("/forgot-password/resend-otp", data);
};

export const resetPassword = async (data) => {
  await fetchCsrfCookie();
  return API.post("/reset-password", data);
};

export const logoutUser = async () => {
  const token =
    localStorage.getItem("magizh_token") ||
    localStorage.getItem("magizh_admin_token") ||
    localStorage.getItem("auth_token");

  // Instantly clear local storage credentials and session caches
  try {
    localStorage.removeItem("magizh_token");
    localStorage.removeItem("magizh_admin_token");
    localStorage.removeItem("magizh_user");
    localStorage.removeItem("magizh_admin_user");
    localStorage.removeItem("auth_token");
    localStorage.removeItem("magizh_guide_token");
    localStorage.removeItem("magizh_guide_user");
    sessionStorage.removeItem("last_me_sync");
    sessionStorage.removeItem("magizh_categories");
    sessionStorage.removeItem("magizh_home_banners");
    clearApiCache();
  } catch (e) {
    // Ignore storage errors
  }

  // Fire-and-forget server token revocation in background without blocking UI
  if (token) {
    API.post("/logout", {}, { timeout: 3000 }).catch(() => {});
  }

  return { status: true };
};

export const getCurrentUser = () => API.get("/me");

export const getPrimePlan = () => API.get("/prime/plan");
export const getPrimeOffers = () => API.get("/prime/offers");
export const getPrimeCategories = (status = "active") => API.get(`/prime/categories${status ? `?status=${status}` : ""}`);
export const joinPrime = (data) => API.post("/prime/join", data);
export const getAdminPrimeMembers = (page = 1) => API.get(`/admin/prime/members?page=${page}`);
export const updateAdminPrimePlan = (data) => API.put("/admin/prime/plan", data);
export const toggleAdminUserPrime = (userId, data) => API.post(`/admin/prime/users/${userId}/toggle`, data);
export const getAdminPrimeCategories = () => API.get("/admin/prime/categories");
export const createAdminPrimeCategory = (data) => API.post("/admin/prime/categories", data);
export const updateAdminPrimeCategory = (id, data) => API.post(`/admin/prime/categories/${id}`, data);
export const deleteAdminPrimeCategory = (id) => API.delete(`/admin/prime/categories/${id}`);

export default API;